/**
 * Screenshots on a trade, and the link that shares it.
 *
 * ## Paste is the primary path
 *
 * A trader takes a screenshot of their platform and wants it on the trade. The
 * file picker works, but it means alt-tabbing to a downloads folder to find an
 * image that is already on the clipboard. So this component listens for a paste
 * while it has focus, and the file picker is the fallback rather than the route.
 *
 * ## What it refuses, and why the refusal is visible
 *
 * PNG, JPEG and WebP. **SVG is deliberately excluded** — it is a document that
 * can carry script, and serving one back from our own origin is a stored XSS.
 * The server enforces this; the client says so before the upload so the refusal
 * arrives as an explanation rather than a 415.
 *
 * ## Sharing is opt-in twice
 *
 * Creating the link is one decision. Including the trader's own note is a
 * second, defaulted off: a chart is not a licence to publish what somebody
 * wrote about their own discipline. Revocation is immediate and total, because
 * the token IS the authorisation — there is no other check behind it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, JSX } from 'react';
import type { AttachmentDto, ShareDto } from '@aegis/api-client';
import { api, ApiError } from '../lib/api.js';
import { authHeaders } from '../lib/credentials.js';
import { apiBase, isDesktop, url as apiUrl } from '../lib/runtime.js';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'] as const;
const MAX_BYTES = 5 * 1024 * 1024;

const EXPIRY_CHOICES: readonly { label: string; days: number | null }[] = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  // Offered last and named honestly. A link that never expires is one the
  // trader forgets they made.
  { label: 'Never expires', days: null },
];

/** Base64 without the `data:` prefix, which the API does not want. */
function toBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.onload = () => {
      const out = String(r.result);
      resolve(out.slice(out.indexOf(',') + 1));
    };
    r.readAsDataURL(file);
  });
}

const kb = (n: number): string =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;

/**
 * The bytes, fetched with credentials and handed to the browser as a blob.
 *
 * An `<img src="/v1/attachments/.../bytes">` works on the web, where the session
 * is a cookie the browser attaches by itself. It does NOT work in the desktop
 * shell, where the page is loaded from `tauri://localhost` and the session is a
 * Bearer header — nothing attaches a header to an image request, so the image
 * would 401 and render broken in exactly the place a trader is most likely to
 * be sitting.
 *
 * So the request goes through `fetch` with the same headers every other request
 * uses, and the result becomes an object URL. Revoked on unmount, because a
 * trader clicking down a table of trades would otherwise accumulate one blob per
 * screenshot for the life of the tab.
 */
function useAttachmentSrc(a: AttachmentDto): string | null {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(apiUrl(a.url), { headers: authHeaders() });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        // A screenshot that will not load is not worth an error banner over the
        // journal entry it belongs to. The caption below it still names it.
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [a.url]);

  return src;
}

function Shot({ a, onDelete }: { a: AttachmentDto; onDelete(): void }): JSX.Element {
  const src = useAttachmentSrc(a);
  return (
    <li>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={a.caption || 'Trade screenshot'} />
        </a>
      ) : (
        <div className="shot-placeholder" aria-hidden="true" />
      )}
      <div className="shot-meta">
        <span className="dim">{a.caption || 'Screenshot'} · {kb(a.byteSize)}</span>
        <button className="btn btn-quiet compact" onClick={onDelete} aria-label="Delete screenshot">
          Delete
        </button>
      </div>
    </li>
  );
}

/**
 * The share link, absolute.
 *
 * `runtime.url()` returns a relative path on the web, which is the right answer
 * for a fetch and the wrong one for a link whose entire purpose is to be pasted
 * somewhere else. On the desktop the API lives on its own origin, and that is
 * the origin serving `/s/:token`.
 */
function absoluteShareUrl(path: string): string {
  if (isDesktop()) return `${apiBase()}${path}`;
  return typeof location === 'undefined' ? path : `${location.origin}${path}`;
}

export function TradeAttachments({ tradeId }: { tradeId: string }): JSX.Element {
  const [shots, setShots] = useState<AttachmentDto[]>([]);
  const [share, setShare] = useState<ShareDto | null>(null);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [expiryDays, setExpiryDays] = useState<number | null>(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([api.attachments(tradeId), api.shares()]);
      setShots(a.attachments);
      setShare(s.shares.find((x) => x.tradeId === tradeId) ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load screenshots.');
    }
  }, [tradeId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setCopied(false); }, [tradeId]);

  const upload = useCallback(async (file: Blob, name = ''): Promise<void> => {
    if (!(ACCEPTED as readonly string[]).includes(file.type)) {
      setError(
        file.type === 'image/svg+xml'
          ? 'SVG is not accepted. It is a document that can carry script, so it '
            + 'is excluded on purpose — export a PNG instead.'
          : 'Screenshots must be PNG, JPEG or WebP.',
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That file is ${kb(file.size)}. The cap is ${kb(MAX_BYTES)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.uploadAttachment({
        tradeId, contentType: file.type, data: await toBase64(file), caption: name,
      });
      setShots((s) => [...s, created]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not upload that.');
    } finally { setBusy(false); }
  }, [tradeId]);

  const onPaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    const item = [...e.clipboardData.items].find((i) => i.kind === 'file');
    const file = item?.getAsFile();
    if (!file) return;
    e.preventDefault();
    void upload(file, 'Pasted');
  };

  const remove = async (id: string): Promise<void> => {
    setShots((s) => s.filter((x) => x.id !== id));
    try { await api.deleteAttachment(id); }
    catch { await load(); }
  };

  const makeShare = async (): Promise<void> => {
    setBusy(true);
    try {
      setShare(await api.createShare(tradeId, includeNotes, expiryDays));
      setCopied(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the link.');
    } finally { setBusy(false); }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    try { await api.revokeShare(tradeId); setShare(null); setCopied(false); }
    finally { setBusy(false); }
  };

  const publicUrl = share ? absoluteShareUrl(share.url) : null;

  return (
    <div className="trade-extras" onPaste={onPaste}>
      <section className="field">
        <span className="field-label">Screenshots</span>

        {shots.length > 0 && (
          <ul className="shot-list">
            {shots.map((a) => <Shot key={a.id} a={a} onDelete={() => void remove(a.id)} />)}
          </ul>
        )}

        <div className="shot-add">
          <button
            className="btn btn-quiet compact"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {busy ? 'Uploading…' : 'Add a screenshot'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED.join(',')}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f, f.name);
              e.target.value = '';
            }}
          />
          <span className="field-hint">
            Or paste one here. PNG, JPEG or WebP, up to {kb(MAX_BYTES)}.
          </span>
        </div>
      </section>

      <section className="field">
        <span className="field-label">Share this trade</span>

        {share ? (
          <>
            <div className="share-link">
              <input className="input mono" readOnly value={publicUrl ?? ''} onFocus={(e) => e.target.select()} />
              <button
                className="btn btn-quiet compact"
                onClick={() => {
                  if (publicUrl) void navigator.clipboard?.writeText(publicUrl).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <span className="field-hint">
              Anyone with this link can read this one trade — the token is the only
              check there is. It shows nothing about your account, your balance or
              your other trades.{' '}
              {share.includeNotes
                ? 'Your note travels with it.'
                : 'Your note is not included.'}
              {share.expiresAt
                ? ` Expires ${new Date(share.expiresAt).toLocaleDateString()}.`
                : ' It does not expire.'}
            </span>
            <button className="btn btn-quiet compact" onClick={() => void revoke()} disabled={busy}>
              Revoke link
            </button>
          </>
        ) : (
          <>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
              />
              <span>Include my note</span>
            </label>
            <label className="field">
              <span className="field-hint">Link lifetime</span>
              <select
                className="input"
                value={expiryDays === null ? 'never' : String(expiryDays)}
                onChange={(e) =>
                  setExpiryDays(e.target.value === 'never' ? null : Number(e.target.value))}
              >
                {EXPIRY_CHOICES.map((c) => (
                  <option key={c.label} value={c.days === null ? 'never' : String(c.days)}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn btn-quiet compact" onClick={() => void makeShare()} disabled={busy}>
              Create a link
            </button>
          </>
        )}
      </section>

      {error && <div className="form-error" role="alert">{error}</div>}
    </div>
  );
}
